import {useState} from 'react';
import {Link, useLocation, useNavigate, useParams} from 'react-router-dom';
import {usePlace, usePlaces} from '../api/places';
import {useFeed} from '../api/posts';
import Button from '../components/Button';
import EmptyState from '../components/feedback/EmptyState';
import ErrorBox from '../components/feedback/ErrorBox';
import {SkeletonBlock, SkeletonList} from '../components/feedback/Skeleton';
import Icon from '../components/Icon';
import TellCard from '../components/tell/TellCard';
import {groupByDay} from '../lib/groupByDay';
import {PLACE_TYPE_ICONS, PLACE_TYPE_LABELS} from '../lib/placeTree';
import {todayLocal} from '../lib/when';
import {EditPlaceSheet} from './PlacesPage';

// One place (wireframe "place"): laid out like tell detail. The photo (its own, else the newest
// tell photo there), what it is and where, soil, size and notes, what is growing there now, an
// area's beds, "New tell here", then every tell made there as the Journal shows them. In Phase 4
// the same content is the map's bed sheet.

const tellsLabel = (n: number) => (n === 0 ? 'no tells yet' : n === 1 ? '1 tell' : `${n} tells`);

export default function PlacePage() {
    const {id} = useParams<{id: string}>();
    const navigate = useNavigate();
    const location = useLocation();
    const detail = usePlace(id);
    const all = usePlaces(true);
    const feed = useFeed({placeId: id});
    const [editing, setEditing] = useState(false);

    const back = () => (location.key !== 'default' ? navigate(-1) : navigate('/garden', {replace: true}));

    if (detail.isError) {
        return (
            <div className="flex flex-col gap-4 pt-4">
                <Link to="/garden" className="meta inline-flex items-center gap-1 no-underline">
                    <Icon name="back" size={16} /> Garden
                </Link>
                <ErrorBox error={detail.error} onRetry={() => void detail.refetch()} />
            </div>
        );
    }

    const data = detail.data;
    const place = data?.place;
    const posts = feed.data?.pages.flatMap(p => p.items) ?? [];
    const facts = place ? ([['Soil', place.soil], ['Size', place.size], ['Notes', place.notes]] as const).filter(([, v]) => v) : [];

    return (
        <div className="settle-in -mx-4 -mt-[max(12px,env(safe-area-inset-top))] flex min-h-dvh flex-col">
            <div className={`relative ${place?.coverUrl ? 'h-[260px] bg-sunken' : 'h-[180px] bg-kind-plain-bg text-kind-plain-fg'}`}>
                {place?.coverUrl ? (
                    <img src={place.coverUrl} alt="" className="h-full w-full object-cover" />
                ) : (
                    <span className="absolute bottom-14 left-4 flex h-[72px] w-[72px] items-center justify-center rounded-[22px] bg-surface/60">
                        <Icon name={place ? PLACE_TYPE_ICONS[place.type] : 'pin'} size={36} />
                    </span>
                )}
                <button type="button" onClick={back} aria-label="Back" className="absolute left-3 top-[max(16px,env(safe-area-inset-top))] flex h-11 w-11 items-center justify-center rounded-pill bg-surface/90 text-ink shadow-1 backdrop-blur-md">
                    <Icon name="back" size={22} />
                </button>
                {place && (
                    <button type="button" onClick={() => setEditing(true)} className="absolute right-3 top-[max(16px,env(safe-area-inset-top))] flex h-11 items-center gap-2 rounded-pill bg-surface/90 px-4 text-[15px] font-[650] text-ink shadow-1 backdrop-blur-md">
                        <Icon name="edit" size={18} /> Edit
                    </button>
                )}
            </div>

            <div className="relative -mt-7 flex flex-1 flex-col gap-[18px] rounded-t-[26px] bg-ground px-4 pb-10 pt-[22px]">
                {!place ? (
                    <>
                        <SkeletonBlock className="h-6 w-1/3" />
                        <SkeletonBlock className="h-8 w-3/4" />
                        <SkeletonList rows={3} />
                    </>
                ) : (
                    <>
                        <div className="flex flex-col gap-[10px]">
                            <div className="meta flex flex-wrap items-center gap-2">
                                <span className="inline-flex h-7 items-center gap-[5px] rounded-pill bg-kind-plain-bg pl-[6px] pr-[10px] text-xs font-bold text-kind-plain-fg">
                                    <Icon name={PLACE_TYPE_ICONS[place.type]} size={16} />
                                    {PLACE_TYPE_LABELS[place.type]}
                                </span>
                                {data?.area ? (
                                    <Link to={`/places/${data.area.id}`} replace className="font-semibold text-shoot-text no-underline">
                                        {data.area.name}
                                    </Link>
                                ) : null}
                                <span>{tellsLabel(place.tellCount)}</span>
                            </div>
                            <h1 className="title m-0">{place.name}</h1>
                        </div>

                        {facts.length > 0 ? (
                            <div className="rows rounded-[18px] border-[1.5px] border-line bg-surface">
                                {facts.map(([label, value]) => (
                                    <div key={label} className="flex min-h-[52px] flex-col justify-center px-[14px] py-2">
                                        <span className="meta">{label}</span>
                                        <span className="whitespace-pre-wrap font-[650]">{value}</span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <button type="button" className="nudge text-left" onClick={() => setEditing(true)}>
                                Add the soil, size and a note or two, and a photo of its own.
                            </button>
                        )}

                        {data && data.growing.length > 0 && (
                            <section className="flex flex-col gap-2">
                                <h2 className="band-label m-0 text-ink-2">Growing here now</h2>
                                <div className="flex flex-wrap gap-2">
                                    {data.growing.map(g => (
                                        <Link key={g.id} to={`/plants/${g.id}`} className="chip pl-[10px] no-underline">
                                            <Icon name={g.isPermanent ? 'tree' : 'leaf'} size={18} className="text-kind-grow-fg" />
                                            {g.label}
                                        </Link>
                                    ))}
                                </div>
                            </section>
                        )}

                        {data && data.spots.length > 0 && (
                            <section className="flex flex-col gap-2">
                                <h2 className="band-label m-0 text-ink-2">Beds and spots</h2>
                                <div className="rows rounded-[18px] border-[1.5px] border-line bg-surface">
                                    {data.spots.map(bed => (
                                        <Link key={bed.id} to={`/places/${bed.id}`} className="flex min-h-[56px] items-center gap-3 px-4 py-2 no-underline">
                                            {bed.coverThumbUrl ? (
                                                <img src={bed.coverThumbUrl} alt="" loading="lazy" className="h-10 w-10 shrink-0 rounded-[11px] object-cover" />
                                            ) : (
                                                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[11px] bg-kind-grow-bg text-kind-grow-fg">
                                                    <Icon name={PLACE_TYPE_ICONS[bed.type]} size={20} />
                                                </span>
                                            )}
                                            <span className="min-w-0 flex-1">
                                                <span className="block truncate font-semibold">{bed.name}</span>
                                                <span className="meta block">{[PLACE_TYPE_LABELS[bed.type], tellsLabel(bed.tellCount)].join(' · ')}</span>
                                            </span>
                                            <Icon name="chevron" size={20} className="shrink-0 text-ink-3" />
                                        </Link>
                                    ))}
                                </div>
                            </section>
                        )}

                        <Link to={`/tell/new?${new URLSearchParams({place: place.id}).toString()}`} className="btn btn-primary no-underline">
                            <Icon name="camera" size={20} /> New tell here
                        </Link>

                        {feed.isPending && <SkeletonList rows={2} kind="card" />}
                        {feed.isError && <ErrorBox error={feed.error} onRetry={() => void feed.refetch()} />}
                        {feed.isSuccess && posts.length === 0 && (
                            <EmptyState icon="pin" tone="plain" title="Nothing told here yet.">
                                Tells that name {place.name}
                                {place.parentId === null ? ' or one of its beds' : ''} will gather here.
                            </EmptyState>
                        )}
                        {groupByDay(posts, todayLocal()).map(group => (
                            <section key={group.day} className="flex flex-col gap-3">
                                <h2 className="m-0 font-display text-lg font-bold">{group.label}</h2>
                                {group.posts.map(post => (
                                    <TellCard key={post.id} post={post} />
                                ))}
                            </section>
                        ))}
                        {feed.hasNextPage && (
                            <Button variant="ghost" busy={feed.isFetchingNextPage} busyLabel="Loading…" onClick={() => feed.fetchNextPage()}>
                                Show older
                            </Button>
                        )}
                    </>
                )}
            </div>

            {editing && place && <EditPlaceSheet key={place.id + place.updatedAt} place={place} all={all.data ?? [place]} onClose={() => setEditing(false)} />}
        </div>
    );
}
